import dynamic from 'next/dynamic';
import { ModalState, modalStateClosed, modalStateOpen } from '@release/shared/utils/modal';
import { PropsWithChildren, createContext, useState } from 'react';

import type { ReactNode } from 'react';
import type { Group } from '@release/connect-gen/legacy/releaseid/useridgw_pb';
import type { Anken } from '@release/connect-gen/legacy/releasepf/anken_pb';

import { SuccessDialog } from '@/components/SuccessDialog';

const DuplicateAnkenSheet = dynamic(() =>
  import('@/features/anken/components/DuplicateAnkenSheet').then((mod) => mod.DuplicateAnkenSheet)
);
const EditAnkenSheet = dynamic(() =>
  import('@/features/anken/components/EditAnkenModal').then((mod) => mod.EditAnkenSheet)
);
interface ModalPortalValue {
  showEditAnkenSheet: (anken: Anken) => void;
  closeEditAnkenSheet: () => void;
  showDuplicateAnkenSheet: (anken: Anken) => void;
  closeDuplicateAnkenSheet: () => void;
  showSuccessDialog: (message: string, delay?: number, detail?: ReactNode) => void;
  closeSuccessDialog: () => void;
}

export const ModalPortalContext = createContext<ModalPortalValue>({
  showEditAnkenSheet: () => {},
  closeEditAnkenSheet: () => {},
  showDuplicateAnkenSheet: () => {},
  closeDuplicateAnkenSheet: () => {},
  showSuccessDialog: () => {},
  closeSuccessDialog: () => {},
});

export type NewAnkenSheetState = ModalState<{
  defaultGroup: string;
}>;
export type EditAnkenSheetState = ModalState<{ anken: Anken }>;
export type DuplicateAnkenSheetState = ModalState<{ anken: Anken }>;
export type CreateProcedureSheetState = ModalState<{ anken: Anken; group: Group }>;
export type SuccessDialogState = ModalState<{ message: string; detail?: ReactNode }>;

export const AnkenModalPortalProvider = ({ children }: PropsWithChildren) => {
  const [editAnkenSheetState, setEditAnkenSheetState] = useState<EditAnkenSheetState>(modalStateClosed);
  const [duplicateAnkenSheetState, setDuplicateAnkenSheetState] = useState<EditAnkenSheetState>(modalStateClosed);
  const [successDialogState, setSuccessDialogState] = useState<SuccessDialogState>(modalStateClosed);

  const showEditAnkenSheet = (anken: Anken) => {
    setEditAnkenSheetState(modalStateOpen({ anken }));
  };
  const closeEditAnkenSheet = () => {
    setEditAnkenSheetState(modalStateClosed);
  };
  const showDuplicateAnkenSheet = (anken: Anken) => {
    setDuplicateAnkenSheetState(modalStateOpen({ anken }));
  };
  const closeDuplicateAnkenSheet = () => {
    setDuplicateAnkenSheetState(modalStateClosed);
  };
  const showSuccessDialog = (message: string, delay?: number, detail?: ReactNode) => {
    const open = () => setSuccessDialogState(modalStateOpen({ message, detail }));
    if (delay) {
      setTimeout(open, delay);
      return;
    }
    open();
  };
  const closeSuccessDialog = () => {
    setSuccessDialogState(modalStateClosed);
  };
  const props = {
    showEditAnkenSheet,
    closeEditAnkenSheet,
    showDuplicateAnkenSheet,
    closeDuplicateAnkenSheet,
    showSuccessDialog,
    closeSuccessDialog,
  };
  return (
    <ModalPortalContext.Provider value={props}>
      {children}
      <EditAnkenSheet state={editAnkenSheetState} onRequestClose={closeEditAnkenSheet} />
      <DuplicateAnkenSheet state={duplicateAnkenSheetState} onClose={closeDuplicateAnkenSheet} />
      <SuccessDialog state={successDialogState} onClose={closeSuccessDialog} />
    </ModalPortalContext.Provider>
  );
};
